/**
 * PhotoCache.js
 * A utility for caching recent property photos to improve performance.
 * Uses a combination of localStorage for metadata and IndexedDB for image blobs.
 */

// Key for localStorage caching
const PHOTO_CACHE_KEY = 'property_photos_cache';
const PHOTO_CACHE_TIMESTAMP_KEY = 'property_photos_cache_timestamp';
const MAX_CACHED_PHOTOS = 20;
const INDEXEDDB_NAME = 'property_photos_db';
const INDEXEDDB_STORE_NAME = 'photo_blobs';
const INDEXEDDB_VERSION = 1;

/**
 * Initialize the IndexedDB database for storing photo blobs
 * @returns {Promise} A promise that resolves when the database is ready
 */
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(INDEXEDDB_NAME, INDEXEDDB_VERSION);

    request.onerror = (event) => {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // Create an object store for photo blobs if it doesn't exist
      if (!db.objectStoreNames.contains(INDEXEDDB_STORE_NAME)) {
        db.createObjectStore(INDEXEDDB_STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
}

/**
 * Save a photo blob to IndexedDB
 * @param {Number} id - The photo ID
 * @param {Blob} blob - The photo blob data
 * @returns {Promise} A promise that resolves when the blob is saved
 */
function savePhotoBlob(id, blob) {
  return new Promise((resolve, reject) => {
    initDB()
      .then((db) => {
        const transaction = db.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(INDEXEDDB_STORE_NAME);

        const request = store.put({ id, blob });

        request.onsuccess = () => resolve();
        request.onerror = (event) => {
          console.error('Error saving photo blob:', event.target.error);
          reject(event.target.error);
        };
      })
      .catch((error) => {
        console.error('Error in savePhotoBlob:', error);
        reject(error);
      });
  });
}

/**
 * Get a photo blob from IndexedDB
 * @param {Number} id - The photo ID
 * @returns {Promise<Blob|null>} A promise that resolves with the blob or null if not found
 */
function getPhotoBlob(id) {
  return new Promise((resolve, reject) => {
    initDB()
      .then((db) => {
        const transaction = db.transaction([INDEXEDDB_STORE_NAME], 'readonly');
        const store = transaction.objectStore(INDEXEDDB_STORE_NAME);

        const request = store.get(id);

        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result.blob);
          } else {
            resolve(null);
          }
        };

        request.onerror = (event) => {
          console.error('Error getting photo blob:', event.target.error);
          reject(event.target.error);
        };
      })
      .catch((error) => {
        console.error('Error in getPhotoBlob:', error);
        reject(error);
      });
  });
}

/**
 * Delete old photo blobs that are no longer in the cache
 * @param {Array} activePhotoIds - Array of photo IDs that should be kept
 * @returns {Promise} A promise that resolves when cleanup is complete
 */
function cleanupOldBlobs(activePhotoIds) {
  return new Promise((resolve, reject) => {
    initDB()
      .then((db) => {
        const transaction = db.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(INDEXEDDB_STORE_NAME);

        // Get all keys (photo IDs) in the store
        const getAllKeysRequest = store.getAllKeys();

        getAllKeysRequest.onsuccess = () => {
          const existingKeys = getAllKeysRequest.result;
          const keysToDelete = existingKeys.filter(
            (key) => !activePhotoIds.includes(key),
          );

          // Delete each blob that's no longer needed
          keysToDelete.forEach((key) => {
            store.delete(key);
          });

          if (keysToDelete.length > 0) {
            console.log(
              `Cleaned up ${keysToDelete.length} old photo blobs from cache`,
            );
          }
          resolve();
        };

        getAllKeysRequest.onerror = (event) => {
          console.error('Error getting keys for cleanup:', event.target.error);
          reject(event.target.error);
        };
      })
      .catch((error) => {
        console.error('Error in cleanupOldBlobs:', error);
        reject(error);
      });
  });
}

/**
 * Save photos to cache (both metadata and blobs)
 * @param {Array} photos - Array of photo objects to cache
 * @returns {Promise} A promise that resolves when caching is complete
 */
async function cachePhotos(photos) {
  try {
    // Extract metadata (without blob URLs which can't be serialized)
    const photoMetadata = photos.map((photo) => ({
      id: photo.id,
      propertyId: photo.propertyId,
      caption: photo.caption,
      photoFile: photo.photoFile,
      modifiedOn: photo.modifiedOn,
      propertyAddress: photo.propertyAddress,
    }));

    // Store metadata in localStorage
    localStorage.setItem(PHOTO_CACHE_KEY, JSON.stringify(photoMetadata));
    localStorage.setItem(PHOTO_CACHE_TIMESTAMP_KEY, new Date().toISOString());

    // Cache each photo blob in IndexedDB
    const photoIds = [];
    const blobPromises = photos.map(async (photo) => {
      if (photo.photoUrl) {
        try {
          // Convert the URL to a blob
          const response = await fetch(photo.photoUrl);
          const blob = await response.blob();

          // Save the blob to IndexedDB
          await savePhotoBlob(photo.id, blob);
          photoIds.push(photo.id);
        } catch (error) {
          console.error(`Error caching blob for photo ${photo.id}:`, error);
        }
      }
    });

    // Wait for all blobs to be saved
    await Promise.all(blobPromises);

    // Clean up old blobs
    await cleanupOldBlobs(photoIds);

    return true;
  } catch (error) {
    console.error('Error caching photos:', error);
    return false;
  }
}

/**
 * Load photos from cache
 * @returns {Array} Array of cached photo metadata (without blob URLs)
 */
function getCachedPhotoMetadata() {
  try {
    const cachedPhotos = localStorage.getItem(PHOTO_CACHE_KEY);
    return cachedPhotos ? JSON.parse(cachedPhotos) : [];
  } catch (error) {
    console.error('Error loading cached photos:', error);
    return [];
  }
}

/**
 * Check if the cache needs to be updated
 * @param {String} lastModifiedDate - ISO date string of the latest photo from the server
 * @returns {Boolean} Whether the cache needs to be updated
 */
function isCacheOutdated(lastModifiedDate) {
  try {
    const cacheTimestamp = localStorage.getItem(PHOTO_CACHE_TIMESTAMP_KEY);
    if (!cacheTimestamp) return true;

    const cacheDate = new Date(cacheTimestamp);
    const lastModDate = new Date(lastModifiedDate);

    // If the most recent photo is newer than our cache timestamp, update the cache
    return lastModDate > cacheDate;
  } catch (error) {
    console.error('Error checking cache timestamp:', error);
    return true; // If there's an error, assume cache needs updating
  }
}

/**
 * Get a specific photo from cache by ID
 * @param {Number} photoId - The ID of the photo to get
 * @returns {Object|null} The photo metadata or null if not found
 */
function getCachedPhotoMetadataById(photoId) {
  try {
    const cachedPhotos = getCachedPhotoMetadata();
    return cachedPhotos.find((photo) => photo.id === photoId) || null;
  } catch (error) {
    console.error('Error getting cached photo by ID:', error);
    return null;
  }
}

/**
 * Check if the cache has photos
 * @returns {Boolean} Whether the cache has photos
 */
function hasCachedPhotos() {
  try {
    const cachedPhotos = localStorage.getItem(PHOTO_CACHE_KEY);
    return cachedPhotos && JSON.parse(cachedPhotos).length > 0;
  } catch (error) {
    console.error('Error checking if cache has photos:', error);
    return false;
  }
}

/**
 * Remove a specific photo from the cache
 * @param {Number} photoId - The ID of the photo to remove
 * @returns {Promise} A promise that resolves when the photo is removed
 */
async function removePhoto(photoId) {
  try {
    // Remove from IndexedDB storage
    await initDB().then((db) => {
      const transaction = db.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(INDEXEDDB_STORE_NAME);
      store.delete(photoId);

      return new Promise((resolve, reject) => {
        transaction.oncomplete = resolve;
        transaction.onerror = reject;
      });
    });

    // Remove from metadata in localStorage
    const cachedPhotos = getCachedPhotoMetadata();
    const updatedPhotos = cachedPhotos.filter((photo) => photo.id !== photoId);
    localStorage.setItem(PHOTO_CACHE_KEY, JSON.stringify(updatedPhotos));

    console.log(`Removed photo ${photoId} from cache`);
    return true;
  } catch (error) {
    console.error(`Error removing photo ${photoId} from cache:`, error);
    return false;
  }
}

// Export methods
export default {
  cachePhotos,
  getCachedPhotoMetadata,
  getCachedPhotoMetadataById,
  isCacheOutdated,
  hasCachedPhotos,
  getPhotoBlob,
  removePhoto,
  MAX_CACHED_PHOTOS,
  PHOTO_CACHE_KEY,
  PHOTO_CACHE_TIMESTAMP_KEY,
};
